import { useState, useEffect, useRef } from "react";
import TabsContainer from "../features/common/TabsContainer";
import StatsCards from "../StatCard";
import StudentHeader from "./StudentHeader";
import { useDispatch, useSelector } from "react-redux";
import {
  addStudentToList,
  setCreateStudentModal,
} from "../../store/studentManagementReducer";
import CreateStudentModal from "./StudentModal/CreateStudentModal";
import {
  addStudent,
  getStudentStats,
  addBulkStudent,
} from "../../application/services/studentManagementService";
import useStudentList from "../hooks/useStudentList";
import { getStudentManagementTabs } from "../constants/StudentManagementConstants";
import "./Style.css";
import { SWAGGER_URL } from "../../common/apiBaseUrl";

const StudentManagementTab = ({ activeMenu }) => {
  const { openStudentModal } = useSelector((state) => state.studentManagement);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("studentList");
  const { fetchStudentList } = useStudentList();
  const hasFetchedInitial = useRef(false);
  const [studentStats, setStudentStats] = useState({});
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkUploadError, setBulkUploadError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!hasFetchedInitial.current) {
      hasFetchedInitial.current = true;
      fetchStudentList(1, false);
      
      const fetchStudentStats = async () => {
        try {
          const response = await getStudentStats();
          const statsData = response?.data || response || {};
          console.log("stats data", statsData);
          setStudentStats(statsData);
        } catch (error) {
          console.error("Error fetching student stats:", error);
        }
      };
      fetchStudentStats();
    }
  }, []);

  const onClickAddStudent = () => {
    dispatch(setCreateStudentModal(true));
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    if (tabKey === "studentList") {
      fetchStudentList(1, false);
    }
  };

  const onAddStudent = async (formData) => {
    const payload = {
      name: formData.name,
      email: formData.email,
      university: formData.university,
      course: formData.course,
      graduation_year: formData.graduation_year,
      gpa: formData.gpa,
      phone_number: formData.phone_number,
      address: formData.address,
      emergency_contact: formData.emergency_contact,
      notes: formData.notes,
    };

    const response = await addStudent(payload);
    const createdStudent =
      response?.data && typeof response.data === "object"
        ? response.data
        : response;

    if (createdStudent) {
      dispatch(addStudentToList(createdStudent));
    }

    return createdStudent;
  };

  const onUpdateStudent = async () => {
    fetchStudentList(1, false, {}, true);
  };

  const tabs = getStudentManagementTabs();

  const uploadCSVToS3 = async (file) => {
    try {
      const formData = new FormData();
      formData.append("folder", "student-bulk-uploads");
      formData.append("file", file);

      const timestamp = Date.now();
      const response = await fetch(
        `${SWAGGER_URL}/storage/add-external`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("File upload failed");
      const data = await response.json();
      return data?.url || data?.fileUrl || null;
    } catch (error) {
      console.error("Error uploading CSV to S3:", error);
      return null;
    }
  };

  const handleCSVFileUpload = async (file) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setBulkUploadError("Please upload a CSV file");
      return;
    }

    setIsBulkUploading(true);
    setBulkUploadError("");

    try {
      const s3Url = await uploadCSVToS3(file);
      
      if (!s3Url) {
        throw new Error("Failed to upload CSV file to S3");
      }

      const payload = {
        additional_param_name: null,
        additional_param_value: null,
        document: s3Url,
        request_type: "BULK_STUDENT_CREATION"
      };

      const response = await addBulkStudent(payload);
      const result = response?.data || response;
      
      if (result) {
        setIsProcessing(true);
        setBulkUploadError("");
        
        setTimeout(() => {
          fetchStudentList(1, false).then(() => {
            setIsProcessing(false);
          }).catch((error) => {
            console.error("Error refreshing student list:", error);
            setIsProcessing(false);
          });
        }, 300000); 
      }
      
      return result;
    } catch (error) {
      const errorMessage = error?.message || error?.data?.message || "Failed to add bulk students. Please try again.";
      setBulkUploadError(errorMessage);
      console.error("Error adding bulk students:", error);
    } finally {
      setIsBulkUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const onAddBulk = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleCSVFileUpload(file);
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={isBulkUploading}
      />
      <div className="student-management-header-container">
        <StudentHeader onClickAddStudent={onClickAddStudent} onAddBulkStudent={onAddBulk}/>
      </div>
      {bulkUploadError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {bulkUploadError}
        </div>
      )}
      {isBulkUploading && (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
          Uploading CSV file and adding students...
        </div>
      )}
      <StatsCards metricsArray={studentStats}/>
      <TabsContainer
        tabs={tabs}
        defaultTab="studentList"
        activeTab={activeTab}
        onActiveTabClick={handleTabChange}
        processingMessage={isProcessing ? "⏳ Processing uploaded data… This may take up to 5 minutes. The table will refresh automatically once ready..." : null}
      />
      {openStudentModal && <CreateStudentModal onAddStudent={onAddStudent} onUpdateStudent={onUpdateStudent} />}
    </>
  );
};

export default StudentManagementTab;
